import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api, { formatErr } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);     // null=loading, false=unauth, obj=auth
  const [tenant, setTenant] = useState(null);
  const [hiddenModules, setHiddenModules] = useState([]);
  const [enabledAddons, setEnabledAddons] = useState([]);
  const [billing, setBilling] = useState(null);
  // Platform profile — used as a fallback for the sidebar app name when
  // the tenant hasn't yet entered their own legal company name under
  // Settings → Company Profile.
  const [platformProfile, setPlatformProfile] = useState(null);
  // Effective per-tenant feature flags — populated from /auth/me. Keys
  // are the 10 feature keys from TENANT_FEATURE_REGISTRY; values are
  // booleans. Used by sidebar + tab-visibility gating.
  const [effectiveFeatures, setEffectiveFeatures] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant || null);
      setHiddenModules(data.hidden_modules || []);
      setEnabledAddons(data.enabled_addons || []);
      setBilling(data.billing || null);
      setPlatformProfile(data.platform_profile || null);
      setEffectiveFeatures(data.effective_features || {});
    } catch {
      setUser(false);
      setTenant(null);
      setHiddenModules([]);
      setEnabledAddons([]);
      setBilling(null);
      setPlatformProfile(null);
      setEffectiveFeatures({});
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password, company_name) => {
    try {
      const body = company_name ? { email, password, company_name } : { email, password };
      const { data } = await api.post("/auth/login", body);
      if (data.access_token) localStorage.setItem("vb_access_token", data.access_token);
      // Fresh sign-in always restarts the onboarding wizard guard AND drops any
      // site selection left by a previous user on this browser — otherwise a
      // stale X-Site-Context can lock the new user out of their own sites.
      try { localStorage.removeItem("vb_active_site"); } catch (e) { /* localStorage may be unavailable */ }
      try { sessionStorage.removeItem("vb_onboarding_paused"); } catch (e) { /* sessionStorage may be unavailable */ }
      await refresh();
      return { ok: true, user: data.user };
    } catch (e) {
      return { ok: false, error: formatErr(e.response?.data?.detail) };
    }
  };

  const registerTenant = async () => ({ ok: false, error: "Self-serve workspace creation is disabled." });

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { console.error("Logout request failed:", e); }
    localStorage.removeItem("vb_access_token");
    try { localStorage.removeItem("vb_active_site"); } catch (e) { /* localStorage may be unavailable */ }
    try { sessionStorage.removeItem("vb_onboarding_paused"); } catch (e) { /* sessionStorage may be unavailable */ }
    setUser(false); setTenant(null);
  };

  const value = useMemo(
    () => ({
      user, tenant, setTenant, refresh, login, registerTenant, logout,
      hiddenModules, enabledAddons, billing, platformProfile, effectiveFeatures,
      isModuleHidden: (key) => hiddenModules.includes(key),
      isAddonEnabled: (key) => enabledAddons.includes(key),
      isFeatureEnabled: (key) => effectiveFeatures[key] !== false, // default-on if unknown
    }),
    [user, tenant, refresh, hiddenModules, enabledAddons, billing, platformProfile, effectiveFeatures],
  );

  return (
    <AuthCtx.Provider value={value}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
